// GPS Metric Configurations
// Centralized configuration for all GPS metrics across different systems

export const GPS_METRIC_CONFIGS = {
  catapult: {
    distance: {
      displayName: "Total Distance",
      field: "session_distance",
      unit: "distance",
      chartType: "bar",
      gaugeType: "game-avg-cat",
      thresholds: {
        veryHigh: 10000,
        high: 5000,
        moderate: 2500,
        low: 0,
      },
    },
    hsr: {
      displayName: "High Speed Running",
      field: "total_absolute_high_speed_distance",
      unit: "distance",
      chartType: "bar",
      gaugeType: "game-hsr-cat",
      thresholds: {
        veryHigh: 1000,
        high: 500,
        moderate: 250,
        low: 0,
      },
    },
    accelerations: {
      displayName: "Accelerations / Decelerations",
      field: "accelerations_total_efforts",
      secondaryField: "decelerations_total_efforts",
      unit: "count",
      chartType: "dual-bar",
      gaugeType: "game-acc-decel-cat",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    intensity: {
      displayName: "Intensity",
      field: "meterage_per_minute",
      unit: "rate",
      chartType: "bar",
      gaugeType: "game-intensity-cat",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    perTopSpeed: {
      displayName: "% Top Speed",
      field: "max_vel_max",
      unit: "percentage",
      chartType: "bar",
      gaugeType: "game-top-speed-per-cat",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    topSpeed: {
      displayName: "Top Speed",
      field: "maximum_velocity",
      unit: "speed",
      chartType: "bar",
      gaugeType: "game-top-speed-cat",
      thresholds: {
        veryHigh: 10,
        high: 5,
        moderate: 2,
        low: 0,
      },
    },
  },

  playermaker: {
    sessionDuration: {
      displayName: "Session Duration",
      field: "phase_duration_min",
      unit: "minutes",
      chartType: "bar",
      thresholds: {
        veryHigh: 200,
        high: 100,
        moderate: 50,
        low: 0,
      },
    },
    distance: {
      displayName: "Total Distance",
      field: "distance_covered",
      unit: "distance",
      chartType: "bar",
      thresholds: {
        veryHigh: 10000,
        high: 5000,
        moderate: 2500,
        low: 0,
      },
    },
    workRate: {
      displayName: "Work Rate",
      field: "work_rate",
      unit: "rate",
      chartType: "bar",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    totalTouches: {
      displayName: "Total Touches",
      field: "total_touches",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 800,
        high: 400,
        moderate: 150,
        low: 0,
      },
    },
    leftLegTouches: {
      displayName: "Left Leg Touches",
      field: "left_leg_touches",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 300,
        high: 150,
        moderate: 50,
        low: 0,
      },
    },
    rightLegTouches: {
      displayName: "Right Leg Touches",
      field: "right_leg_touches",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 300,
        high: 150,
        moderate: 50,
        low: 0,
      },
    },
    totalPasses: {
      displayName: "Total Passes",
      field: "releases",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 250,
        high: 125,
        moderate: 75,
        low: 0,
      },
    },
    leftPasses: {
      displayName: "Left Passes",
      field: "releases_left",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    rightPasses: {
      displayName: "Right Passes",
      field: "releases_right",
      unit: "count",
      chartType: "bar",
      thresholds: {
        veryHigh: 100,
        high: 50,
        moderate: 25,
        low: 0,
      },
    },
    topSpeed: {
      displayName: "Top Speed",
      field: "top_speed",
      unit: "speed",
      chartType: "bar",
      thresholds: {
        veryHigh: 10,
        high: 5,
        moderate: 2,
        low: 0,
      },
    },
  },
};

// Unit formatting functions
export const UNIT_FORMATTERS = {
  distance: (value, unit = "Meters") => {
    switch (unit) {
      case "Meters":
        return Math.round(value).toLocaleString() + " m";
      case "Km":
        return (value / 1000).toFixed(2) + " km";
      case "Miles":
        return (value * 0.000621371).toFixed(2) + " mi";
      default:
        return Math.round(value).toLocaleString() + " m";
    }
  },

  speed: (value, unit = "m/s") => {
    switch (unit) {
      case "m/s":
        return value.toFixed(2) + " m/s";
      case "Mph":
        return (value * 2.23694).toFixed(2) + " mph";
      default:
        return value.toFixed(2) + " m/s";
    }
  },

  rate: (value) => {
    return Math.round(value).toLocaleString() + " m/min";
  },

  count: (value) => {
    return Math.round(value).toLocaleString();
  },

  minutes: (value) => {
    return Math.round(value) + " mins";
  },

  percentage: (value) => {
    return Math.round(value) + "%";
  },
};

// Chart color configurations
export const CHART_COLORS = {
  backgroundColorMap: {
    3: "#ec1111", // Very High
    2: "#DC7C24", // High
    1: "#FFC107", // Moderate
    0: "#4CAF50", // Low
  },
};

// Get color for value based on thresholds
export const getColorForValue = (value, thresholds) => {
  const colors = CHART_COLORS.backgroundColorMap;
  if (value >= thresholds.veryHigh) return colors[3];
  if (value >= thresholds.high) return colors[2];
  if (value >= thresholds.moderate) return colors[1];
  return colors[0];
};
